import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon, 
  GlobeAltIcon,
  ChevronRightIcon 
} from '@heroicons/react/24/outline';
import AnimatedImage from '../ui/AnimatedImage';
import BookDemoModal from '../ui/BookDemoModal';
import homePageImg from '../../assets/homepage.png';

export default function Hero() {
  const [modalOpen, setModalOpen] = useState(false);

  const platforms = [
    { icon: GlobeAltIcon, name: 'Web', color: 'text-blue-500' },
    { icon: ComputerDesktopIcon, name: 'Windows', color: 'text-green-500' },
    { icon: DevicePhoneMobileIcon, name: 'Mobile', color: 'text-purple-500' },
  ];

  return (
    <div className="relative isolate pt-14 overflow-hidden">
      <BookDemoModal open={modalOpen} onClose={() => setModalOpen(false)} />
      
      {/* Background gradient */}
      <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
        <div
          className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary-400 to-secondary-400 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>

      <div className="py-24 sm:py-32 lg:pb-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl flex flex-col-reverse lg:flex-row items-center gap-12 text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex-1 space-y-8"
            >
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="inline-flex items-center rounded-full bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 ring-1 ring-inset ring-primary-700/20"
                >
                  <span className="mr-2">🚀</span>
                  Innovative Technology Solutions
                </motion.div>
                
                <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl">
                  <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                    Transform Your Business
                  </span>
                  <br />
                  <span className="text-gray-900">with Autiin</span>
                </h1>
                
                <p className="mt-6 text-xl leading-8 text-gray-600 max-w-2xl">
                  Empowering hospitality and business sectors with cutting-edge software solutions. 
                  From channel management to inventory control, we deliver scalable, secure, and 
                  user-friendly platforms across all devices.
                </p>
              </div>

              {/* Platform compatibility */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex items-center justify-center lg:justify-start space-x-6"
              >
                <span className="text-sm font-medium text-gray-500">Available on:</span>
                <div className="flex space-x-4">
                  {platforms.map((platform, index) => (
                    <div key={platform.name} className="flex items-center space-x-2">
                      <platform.icon className={`h-5 w-5 ${platform.color}`} />
                      <span className="text-sm font-medium text-gray-700">{platform.name}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
              >
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="group relative rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 px-8 py-4 text-lg font-semibold text-white shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200"
                >
                  Request Demo
                  <ChevronRightIcon className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" />
                </button>
                <a 
                  href="#solutions" 
                  className="text-lg font-semibold text-gray-900 hover:text-primary-600 transition-colors duration-200 flex items-center"
                >
                  Explore Solutions
                  <ChevronRightIcon className="ml-1 h-5 w-5" />
                </a>
              </motion.div>
            </motion.div>

            <motion.div 
              className="flex-1 flex justify-center mb-10 lg:mb-0"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-2xl blur-xl opacity-20 animate-pulse"></div>
                <AnimatedImage
                  src={homePageImg}
                  alt="Autiin Dashboard Screenshot"
                  animationType="zoomIn"
                  className="relative w-full max-w-lg rounded-2xl shadow-2xl border border-gray-100"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]">
        <div
          className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-secondary-400 to-primary-400 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
        />
      </div>
    </div>
  );
} 